import {ExternalInfo} from "crqlar-react-common/dist/models/common/external.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {useTranslation} from "react-i18next";
import {Stack, Switch} from "@mui/material";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import React, {useState} from "react";
import ImportLogPreviewDialog from "./ImportLogPreviewDialog";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";

interface Props {
    externalInfo?: ExternalInfo[];
    setExternalInfo?: (externalInfo: ExternalInfo[]) => void;
    additionalExternalInfo?: ExternalInfo[];
}

const ExternalInfoPreview = (props: Props) => {

    const {t} = useTranslation()
    const [selectedExternalInfo, setSelectedExternalInfo] = useState<ExternalInfo>()
    const externalInfo = props.externalInfo ?? []
    const additionalExternalInfo = props.additionalExternalInfo ?? []
    const hasPrimaryExternalInfo = !!externalInfo.length
    const hasAdditionalExternalInfo = !!additionalExternalInfo.length

    if (!hasPrimaryExternalInfo && !hasAdditionalExternalInfo) {
        return null
    }

    const renderExternalInfo = (index: number, info: ExternalInfo, showIgnoreToggle: boolean) => (
        <Stack key={info.combinedKey} gap={0.5}>

            {
                showIgnoreToggle &&
                <Stack direction={'row'} gap={1} alignItems={'center'}>
                    <Typography sx={{minWidth: 100}}>
                        {t('Ignore updates')}:
                    </Typography>
                    <Switch
                        id={'ignore-updates'}
                        checked={info.ignoreUpdates}
                        onChange={(e) => {
                            const updated = {
                                ...info,
                                ignoreUpdates: e.target.checked,
                            }

                            const newExternalInfos = [...externalInfo]
                            newExternalInfos[index] = updated
                            props.setExternalInfo?.(newExternalInfos)
                        }}
                    />
                </Stack>
            }

            <Stack direction={'row'} gap={1}>
                <Typography sx={{minWidth: 100}}>
                    {t('Provider')}:
                </Typography>
                <Typography sx={{fontWeight: 'bold'}} data-testid={`external-info-provider`}>
                    {info.provider}
                </Typography>
            </Stack>

            <Stack direction={'row'} gap={1}>
                <Typography sx={{minWidth: 100}}>
                    {t('Id')}:
                </Typography>
                <Typography sx={{fontWeight: 'bold'}} data-testid={`external-info-id`}>
                    {info.id}
                </Typography>
            </Stack>

            <Stack direction={'row'} gap={1}>
                <Typography sx={{minWidth: 100}}>
                    {t('Id type')}:
                </Typography>
                <Typography sx={{fontWeight: 'bold'}} data-testid={`external-info-id-type`}>
                    {info.idType}
                </Typography>
            </Stack>

            <Stack direction={'row'} gap={1}>
                <Typography sx={{minWidth: 100}}>
                    {t('Property id')}:
                </Typography>
                <Typography sx={{fontWeight: 'bold'}} data-testid={`external-info-property-id`}>
                    {info.propertyId}
                </Typography>
            </Stack>

            {
                !!info.createDate &&
                <Stack direction={'row'} gap={1}>
                    <Typography sx={{minWidth: 100}}>
                        {t('Create date')}:
                    </Typography>
                    <Typography sx={{fontWeight: 'bold'}} data-testid={`external-info-create-date`}>
                        {formatDateDMYHM(info.createDate)}
                    </Typography>
                </Stack>
            }

            {
                !!info.updateDate &&
                <Stack direction={'row'} gap={1}>
                    <Typography sx={{minWidth: 100}}>
                        {t('Update date')}:
                    </Typography>
                    <Typography sx={{fontWeight: 'bold'}} data-testid={`external-info-update-date`}>
                        {formatDateDMYHM(info.updateDate)}
                    </Typography>
                </Stack>
            }

            <Stack direction={'row'} gap={1} alignItems={'center'}>
                <Typography sx={{minWidth: 100}}>
                    {t('Import log id')}:
                </Typography>
                <Typography sx={{fontWeight: 'bold'}} data-testid={`external-info-import-log-id`}>
                    {info.importLogId}
                </Typography>

                {
                    !!info.importLogId &&
                    <Button
                        size={'small'}
                        sx={{ml: 'auto'}}
                        onClick={() => {
                            setSelectedExternalInfo(info)
                        }}
                    >
                        {t('View')}
                    </Button>
                }
            </Stack>

        </Stack>
    )

    return (
        <>
            {
                hasPrimaryExternalInfo &&
                <AdditionalProperties title={t('External references (primary)')} variant={'outlined'}>
                    {externalInfo.map((info, index) => renderExternalInfo(index, info, true))}
                </AdditionalProperties>
            }

            {
                hasAdditionalExternalInfo &&
                <AdditionalProperties title={t('External references (additional)')} variant={'outlined'}>
                    {additionalExternalInfo.map((info, index) => renderExternalInfo(index, info, false))}
                </AdditionalProperties>
            }

            <ImportLogPreviewDialog
                open={!!selectedExternalInfo?.importLogId}
                onClose={() => setSelectedExternalInfo(undefined)}
                importLogId={selectedExternalInfo?.importLogId}
            />
        </>
    )

}

export default ExternalInfoPreview;
